import React from "react";
import {Card, Typography, Space, Badge, Tooltip} from "antd";
import {
    HeartOutlined,
    MessageOutlined,
    CheckCircleFilled,
    MoreOutlined,
} from "@ant-design/icons";
import threadsIcon from "../../pages/icons/threads-mini.svg";
import {useTranslation} from "react-i18next";
import {splitMultilangForThreads, addLangPrefix} from "../../utils/TextUtils/threadsSplitUtils";

const THREADS_CHAR_LIMIT = 500;


export default function ThreadsPreview({
                                           captionSource = "",
                                           attachmentSource = "",
                                           text = "",

                                           images = [],
                                           videos = [],
                                           videoUrl,
                                           profileName = "Username",
                                           threadsHandle = "username",
                                           verified = false,
                                       }) {
    const dateStr = React.useMemo(() => formatDate(new Date()), []);

    const attach = String(attachmentSource || text || "").trim();
    const caption = String(captionSource || "").trim();


    const media = React.useMemo(() => {
        const vids = [];
        if (videoUrl) vids.push({type: "video", url: videoUrl});
        for (const v of videos || []) {
            if (v?.url) vids.push({type: "video", url: v.url});
            else if (typeof v === "string") vids.push({type: "video", url: v});
        }
        const imgs = (images || []).map((i) =>
            typeof i === "string" ? {type: "image", url: i} : {type: "image", url: i?.url}
        );
        return [...vids, ...imgs].filter((m) => !!m.url).slice(0, 20);
    }, [images, videos, videoUrl]);

    const hasMedia = media.length > 0;

    const attachLen = attach.length;
    const isShortTextOnly = !hasMedia && attachLen > 0 && attachLen < THREADS_CHAR_LIMIT;

    const useAttachmentInPostCard = !hasMedia && attachLen >= THREADS_CHAR_LIMIT;

    const mlParts = React.useMemo(
        () => splitMultilangForThreads(attach, THREADS_CHAR_LIMIT),
        [attach]
    );

    const decoratedParts = React.useMemo(
        () => addLangPrefix(mlParts),
        [mlParts]
    );

    const headText = React.useMemo(() => {
        if (!hasMedia) {
            return isShortTextOnly ? attach : (caption || attach);
        }
        return (decoratedParts[0]?.text || "").trim();
    }, [hasMedia, isShortTextOnly, attach, caption, decoratedParts]);

    const tailParts = React.useMemo(() => {
        if (!hasMedia) return [];
        return decoratedParts.slice(1).map(p => p.text);
    }, [hasMedia, decoratedParts]);


    return (
        <div style={{
            display: "grid",
            gap: 10,
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",
            boxSizing: "border-box"
        }}>
            <PostCard
                text={headText}
                media={media}
                textAttachment={useAttachmentInPostCard}
                attachmentText={attach}
                dateStr={dateStr}
                profileName={profileName}
                threadsHandle={threadsHandle}
                verified={verified}
            />

            {!!tailParts.length && tailParts.map((chunk, idx) => (
                <ReplyCard
                    key={idx}
                    text={chunk}
                    dateStr={dateStr}
                    profileName={profileName}
                    threadsHandle={threadsHandle}
                    verified={verified}
                />
            ))}
        </div>
    );
}

function AvatarBubble({name}) {
    const letter = (name || "U").trim().charAt(0).toUpperCase();
    return (
        <Badge count="" style={{backgroundColor: "transparent"}} offset={[-4, 8]}>
            <div
                style={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    background: "#f3f4f6",
                    border: "1px solid #e5e7eb",
                    display: "grid",
                    placeItems: "center",
                    color: "#111827",
                    fontWeight: 700,
                }}
            >
                {letter}
            </div>
        </Badge>
    );
}

function MediaScroller({media}) {
    const containerRef = React.useRef(null);

    React.useEffect(() => {
        const root = containerRef.current;
        if (!root) return;
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    const el = e.target.querySelector("video");
                    if (!el) return;
                    if (!e.isIntersecting && !el.paused) el.pause();
                });
            },
            {root, threshold: 0.2}
        );
        const cards = root.querySelectorAll("[data-media-card]");
        cards.forEach((c) => io.observe(c));
        return () => io.disconnect();
    }, [media]);

    return (
        <div
            ref={containerRef}
            style={{
                display: "flex",
                gap: 8,
                overflowX: "auto",
                paddingBottom: 6,
                scrollbarWidth: "thin",
                WebkitOverflowScrolling: "touch",
            }}
        >
            {media.map((m, i) => (
                <div
                    key={i}
                    data-media-card
                    style={{
                        flex: "0 0 auto",
                        width: 320,
                        height: 320,
                        borderRadius: 10,
                        overflow: "hidden",
                        backgroundColor: "#000",
                        border: "1px solid #e5e7eb",
                        display: "flex",
                        alignItems: "stretch",
                        justifyContent: "center",
                    }}
                >
                    {m.type === "video" ? (
                        <video
                            src={m.url}
                            controls
                            playsInline
                            preload="metadata"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                            }}
                        />
                    ) : (
                        <img
                            src={m.url}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            fetchpriority="low"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                                imageRendering: "auto",
                                transform: "translateZ(0)",
                                backfaceVisibility: "hidden",
                                willChange: "transform"
                            }}
                        />
                    )}
                </div>
            ))}
        </div>
    );
}

function formatDate(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

function PostCard({
                      text,
                      media,
                      textAttachment = false,
                      attachmentText = "",
                      dateStr,
                      profileName,
                      threadsHandle,
                      verified
                  }) {
    const hasMedia = media && media.length > 0;

    return (
        <Card
            bordered
            style={{
                background: "#ffffff",
                borderRadius: 12,
                padding: 12,
                color: "#1f2328",
                borderColor: "#e5e7eb",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
            }}
            bodyStyle={{padding: 12}}
        >
            <div style={{display: "flex", gap: 12, alignItems: "flex-start"}}>
                <AvatarBubble name={profileName}/>
                <div style={{flex: 1, minWidth: 0}}>
                    <HeaderLine
                        profileName={profileName}
                        threadsHandle={threadsHandle}
                        verified={verified}
                        dateStr={dateStr}
                    />

                    {(text || "").trim() && (
                        <Typography.Paragraph style={{margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.45}}>
                            {(text || "").trim()}
                        </Typography.Paragraph>
                    )}
                    {textAttachment && (
                        <AttachmentText text={attachmentText || ""}/>
                    )}

                    {!!hasMedia && <MediaScroller media={media}/>}

                    <ActionsBar/>
                </div>
            </div>
        </Card>
    );
}

function ReplyCard({text, dateStr, profileName, threadsHandle, verified}) {
    return (
        <div style={{display: "grid", gridTemplateColumns: "36px 1fr", gap: 12, maxWidth: "100%", minWidth: 0}}>
            <div style={{position: "relative"}}>
                <AvatarBubble name={profileName}/>
                <div aria-hidden
                     style={{position: "absolute", top: 40, bottom: -4, left: 18, width: 2, background: "#e5e7eb"}}/>
            </div>

            <Card size="small" bordered
                  style={{borderRadius: 10, borderColor: "#e5e7eb", width: "100%", maxWidth: "100%"}}
                  bodyStyle={{padding: 12}}>
                <HeaderLine
                    profileName={profileName}
                    threadsHandle={threadsHandle}
                    verified={verified}
                    dateStr={dateStr}
                />

                <Typography.Paragraph style={{margin: 0, whiteSpace: "pre-wrap", lineHeight: 1.45}}>
                    {(text || "").trim()}
                </Typography.Paragraph>

                <ActionsBar/>
            </Card>
        </div>
    );
}

function AttachmentText({text, limit = THREADS_CHAR_LIMIT}) {
    const [expanded, setExpanded] = React.useState(false);
    const {t} = useTranslation();

    const firstN = React.useCallback((s, n) => Array.from(String(s)).slice(0, n).join(""), []);
    const full = String(text || "").trim();
    const needsClamp = Array.from(full).length > limit;

    const shown = expanded || !needsClamp
        ? full
        : firstN(full, limit).trimEnd() + "…";

    return (
        <div
            style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: "10px 14px",
                whiteSpace: "pre-wrap",
                color: "#1f2328",
                lineHeight: 1.5,
                fontSize: 14,
                marginTop: 6,
            }}
        >
            {shown}
            {needsClamp && (
                <div style={{marginTop: 8}}>
          <span
              onClick={() => setExpanded((v) => !v)}
              style={{
                  cursor: "pointer",
                  color: "#2563eb",
                  fontWeight: 500,
                  userSelect: "none",
              }}
          >
            {expanded ? t("post.rollUp") : t("post.showMore")}
          </span>
                </div>
            )}
        </div>
    );
}

function ActionsBar() {
    return (
        <div style={{display: "flex", gap: 14, alignItems: "center", color: "#9aa0a6", marginTop: 8}}>
            <Space size={6}>
                <HeartOutlined/>
                <span>1</span>
            </Space>
            <Space size={6}>
                <MessageOutlined/>
                <span>2</span>
            </Space>
        </div>
    );
}

function HeaderLine({profileName, threadsHandle, verified, dateStr}) {
    return (
        <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 8}}>
            <Typography.Text style={{color: "#111827", fontWeight: 600}}>
                {profileName}
            </Typography.Text>
            {verified && (
                <Tooltip title="Verified">
                    <CheckCircleFilled style={{color: "#2563eb", fontSize: 14}}/>
                </Tooltip>
            )}
            <Typography.Text type="secondary" style={{fontSize: 12}}>
                {dateStr}
            </Typography.Text>
            <div style={{marginLeft: "auto", display: "flex", alignItems: "center", gap: 8}}>
                <MoreOutlined style={{color: "#9aa0a6"}}/>
            </div>
        </div>
    );
}

